import React, { useState, useEffect } from 'react';
import { db, storage } from '../../firebase/config';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { PenLine, Upload, Trash2, Loader } from 'lucide-react';
import PageHeader from '../common/PageHeader';

// 기공소: 필기주문 커스텀 양식 업로드
// 등록하면 치과가 이 기공소로 필기주문할 때 이 양식이 배경으로 사용됨
function LabHandwrittenTemplate({ user }) {
    const targetUserId = user?.companyId || user?.uid;
    const [templateUrl, setTemplateUrl] = useState(null);
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        if (!targetUserId) return;
        getDoc(doc(db, 'users', targetUserId)).then(snap => {
            setTemplateUrl(snap.data()?.handwrittenTemplateUrl || null);
        });
    }, [targetUserId]);

    const handleUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!/image\/(png|jpeg)/.test(file.type)) { alert('PNG 또는 JPG 이미지만 업로드할 수 있습니다.'); return; }
        if (file.size > 10 * 1024 * 1024) { alert('10MB 이하 이미지만 가능합니다.'); return; }
        setUploading(true);
        try {
            const storageRef = ref(storage, `labOrderTemplates/${targetUserId}/template_${Date.now()}.png`);
            await uploadBytes(storageRef, file);
            const url = await getDownloadURL(storageRef);
            await updateDoc(doc(db, 'users', targetUserId), { handwrittenTemplateUrl: url });
            setTemplateUrl(url);
        } catch (err) {
            console.error('양식 업로드 실패:', err);
            alert('업로드에 실패했습니다.');
        } finally {
            setUploading(false);
            e.target.value = '';
        }
    };

    const handleRemove = async () => {
        if (!window.confirm('커스텀 양식을 삭제할까요? 치과에는 덴클 표준 양식이 표시됩니다.')) return;
        await updateDoc(doc(db, 'users', targetUserId), { handwrittenTemplateUrl: null });
        setTemplateUrl(null);
    };

    return (
        <div style={styles.container}>
            <PageHeader icon={PenLine} title="필기주문 양식" />
            <div style={styles.card}>
                <div style={{ fontSize: 14, color: '#334155', lineHeight: 1.7 }}>
                    치과가 태블릿 필기로 주문할 때 배경으로 깔리는 의뢰서 양식입니다.<br />
                    기공소에서 쓰던 종이 의뢰서를 <b>스캔 이미지(PNG/JPG, 세로 A4 권장)</b>로 올리면
                    거래 치과의 필기 화면에 그대로 나타납니다. 등록하지 않으면 덴클 표준 양식이 사용됩니다.
                </div>

                <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                    <label style={styles.uploadBtn}>
                        {uploading ? <Loader size={15} style={{ animation: 'spin 1s linear infinite' }} /> : <Upload size={15} />}
                        {uploading ? '업로드 중...' : (templateUrl ? '양식 변경' : '양식 업로드')}
                        <input type="file" accept="image/png,image/jpeg" onChange={handleUpload} style={{ display: 'none' }} disabled={uploading} />
                    </label>
                    {templateUrl && (
                        <button style={styles.removeBtn} onClick={handleRemove}>
                            <Trash2 size={15} /> 삭제
                        </button>
                    )}
                </div>
            </div>

            <div style={styles.previewTitle}>{templateUrl ? '현재 등록된 양식' : '미리보기 (덴클 표준 양식 사용 중)'}</div>
            {templateUrl ? (
                <img src={templateUrl} alt="필기주문 양식" style={styles.preview} />
            ) : (
                <div style={styles.emptyPreview}>커스텀 양식이 없습니다 — 치과에는 덴클 표준 양식이 표시됩니다</div>
            )}
            <style>{'@keyframes spin { to { transform: rotate(360deg); } }'}</style>
        </div>
    );
}

const styles = {
    container: { maxWidth: 700, margin: '0 auto', padding: '0 16px 40px' },
    card: { backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20, marginTop: 16 },
    uploadBtn: {
        display: 'flex', alignItems: 'center', gap: 7,
        backgroundColor: '#6366f1', color: '#fff', borderRadius: 9,
        padding: '10px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
    },
    removeBtn: {
        display: 'flex', alignItems: 'center', gap: 6,
        backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca',
        borderRadius: 9, padding: '10px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
    },
    previewTitle: { fontSize: 14, fontWeight: 700, color: '#334155', margin: '22px 0 10px' },
    preview: { width: '100%', border: '1px solid #e2e8f0', borderRadius: 10, backgroundColor: '#fff' },
    emptyPreview: {
        padding: '50px 20px', textAlign: 'center', fontSize: 13, color: '#94a3b8',
        border: '1px dashed #cbd5e1', borderRadius: 10, backgroundColor: '#f8fafc',
    },
};

export default LabHandwrittenTemplate;
